<template>
  <div class="analysis-reference-embed">
    <div v-if="loading" class="embed-state">
      <v-progress-circular indeterminate color="#1e3a5f" size="36" />
      <span>계정별내역 불러오는 중...</span>
    </div>
    <div v-else-if="error" class="embed-state is-error">
      <p>{{ error }}</p>
      <button type="button" class="embed-retry" @click="load">다시 시도</button>
    </div>

    <div v-else-if="data" class="aar">
      <!-- 화면 안내 -->
      <div class="aar-guide">
        <div class="aar-guide-row">
          <span class="aar-guide-label">이 화면은</span>
          <p>이 달 통장내역을 <strong>계정과목별로 묶어</strong> 얼마 들어오고 나갔는지 보여주는 <strong>조회 전용</strong> 화면입니다. 여기서 수정하지 않고, 정리한 결과를 확인만 합니다.</p>
        </div>
        <div class="aar-guide-row">
          <span class="aar-guide-label">쓰이는 곳</span>
          <p>월 마감 전 수입·지출 검토 · 계정별 합계 확인 · 인건비지출비율 점검</p>
        </div>
      </div>

      <!-- 상단 요약 -->
      <header class="aar-summary">
        <div class="aar-card is-income">
          <span class="aar-card-label">수입</span>
          <strong>{{ formatWon(data.summary?.incomeTotal) }}</strong>
          <span class="aar-card-count">{{ (data.summary?.income || []).length }}개 계정</span>
        </div>
        <div class="aar-card is-expense">
          <span class="aar-card-label">지출</span>
          <strong>{{ formatWon(data.summary?.expenseTotal) }}</strong>
          <span class="aar-card-count">{{ (data.summary?.expense || []).length }}개 계정</span>
        </div>
        <div class="aar-card">
          <span class="aar-card-label">수입 − 지출</span>
          <strong>{{ formatWon(balance) }}</strong>
          <span class="aar-card-count">{{ periodLabel }}</span>
        </div>
        <button
          v-if="data.laborRatioDetail"
          type="button"
          class="aar-labor"
          :class="data.laborRatioDetail.met ? 'is-ok' : 'is-warn'"
          @click="scrollTo('labor')"
        >
          <span class="aar-card-label">인건비지출비율</span>
          <strong>{{ laborRatioText }}</strong>
          <span class="aar-labor-badge">{{ data.laborRatioDetail.met ? '충족' : '미충족' }}</span>
        </button>
      </header>

      <div class="aar-body">
        <!-- 좌측 목차 -->
        <aside class="aar-toc" aria-label="계정 목록">
          <div class="aar-toc-head">
            <input v-model="tocFilter" type="text" class="aar-toc-search" placeholder="계정 검색" />
            <button type="button" class="aar-toc-toggle" @click="toggleAll">
              {{ allExpanded ? '모두 접기' : '모두 펼치기' }}
            </button>
          </div>
          <nav class="aar-toc-list">
            <template v-if="filteredIncome.length">
              <p class="aar-toc-group">수입</p>
              <button
                v-for="acc in filteredIncome"
                :key="acc.id"
                type="button"
                class="aar-toc-item"
                :class="{ active: isExpanded(acc.id) }"
                @click="scrollTo(acc.id)"
              >
                <span class="aar-toc-name">{{ acc.sname }}</span>
                <span class="aar-toc-amt">{{ formatWon(acc.amount) }}</span>
              </button>
            </template>
            <template v-if="filteredExpense.length">
              <p class="aar-toc-group">지출</p>
              <button
                v-for="acc in filteredExpense"
                :key="acc.id"
                type="button"
                class="aar-toc-item"
                :class="{ active: isExpanded(acc.id) }"
                @click="scrollTo(acc.id)"
              >
                <span class="aar-toc-name">{{ acc.sname }}</span>
                <span class="aar-toc-amt">{{ formatWon(acc.amount) }}</span>
              </button>
            </template>
            <p v-if="!filteredIncome.length && !filteredExpense.length" class="aar-toc-empty">
              검색 결과 없음
            </p>
          </nav>
        </aside>

        <!-- 본문 -->
        <main ref="docRef" class="aar-doc">
          <!-- 인건비비율 -->
          <section v-if="data.laborRatioDetail" id="sec-labor" class="aar-labor-detail">
            <button type="button" class="aar-acc-head" @click="toggleExpand('labor')">
              <v-icon size="18" class="aar-chevron" :class="{ open: isExpanded('labor') }">
                mdi-chevron-down
              </v-icon>
              <span class="aar-acc-title">인건비지출비율</span>
              <span class="aar-acc-amount" :class="data.laborRatioDetail.met ? 'ok' : 'warn'">
                {{ data.laborRatioDetail.met ? '충족' : '미충족' }} · 기준 {{ data.laborRatioDetail.requiredPercent }}%
              </span>
            </button>
            <div v-show="isExpanded('labor')" class="aar-acc-body">
              <p class="aar-note">{{ data.laborRatioDetail.formula }}</p>
              <div class="aar-steps">
                <div v-for="step in data.laborRatioDetail.steps || []" :key="step.key" class="aar-step" :class="{ warn: step.warn }">
                  <span>{{ step.label }}<em v-if="step.hint"> · {{ step.hint }}</em></span>
                  <strong>{{ step.text != null ? step.text : formatWon(step.amount) }}</strong>
                </div>
              </div>

              <details v-if="(data.laborRatioDetail.monthlyRows || []).length" class="aar-sub">
                <summary>월별 추이 (당월 · 누적)</summary>
                <table class="aar-table">
                  <thead>
                    <tr>
                      <th>월</th>
                      <th class="num">당월 수입</th>
                      <th class="num">당월 지출</th>
                      <th class="num">당월 비율</th>
                      <th class="num">누적 비율</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in data.laborRatioDetail.monthlyRows" :key="row.month" :class="{ 'is-bad': row.cumMet === false }">
                      <td>{{ row.label }}</td>
                      <td class="num">{{ formatWon(row.income) }}</td>
                      <td class="num">{{ formatWon(row.expense) }}</td>
                      <td class="num">{{ row.monthRatio != null ? row.monthRatio + '%' : '-' }}</td>
                      <td class="num">{{ row.cumRatio != null ? row.cumRatio + '%' : '-' }}</td>
                    </tr>
                  </tbody>
                </table>
              </details>

              <details v-if="data.laborRatioDetail.expense?.fromWage && (data.laborRatioDetail.expense.wageRows || []).length" class="aar-sub">
                <summary>인건비지출 구성 (임금자료)</summary>
                <table class="aar-table">
                  <thead>
                    <tr>
                      <th>연월</th>
                      <th>사업구분</th>
                      <th class="num">인건비</th>
                      <th class="num">인건비가산</th>
                      <th class="num">합계</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, i) in data.laborRatioDetail.expense.wageRows" :key="i">
                      <td>{{ row.label || row.yearMonth }}</td>
                      <td>{{ row.bizGubun || '-' }}</td>
                      <td class="num">{{ formatWon(row.laborCost) }}</td>
                      <td class="num">{{ formatWon(row.laborAdd) }}</td>
                      <td class="num">{{ formatWon(row.amount) }}</td>
                    </tr>
                  </tbody>
                </table>
                <p class="aar-note">
                  인건비 {{ formatWon(data.laborRatioDetail.expense.wageCostTotal) }}
                  + 인건비가산 {{ formatWon(data.laborRatioDetail.expense.wageAddTotal) }}
                  = {{ formatWon(data.laborRatioDetail.expense.wageTotal) }}
                </p>
              </details>

              <details class="aar-sub">
                <summary>인건비수입 구성 · {{ formatWon(data.laborRatioDetail.income?.total) }}</summary>
                <p class="aar-note">{{ data.laborRatioDetail.income?.note }}</p>
                <table v-if="(data.laborRatioDetail.income?.groups || []).length" class="aar-table">
                  <thead>
                    <tr>
                      <th v-if="data.showAccountCodes">코드</th>
                      <th>계정</th>
                      <th class="num">합계</th>
                      <th class="num">건수</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(g, i) in data.laborRatioDetail.income.groups" :key="i">
                      <td v-if="data.showAccountCodes">{{ g.scode || '-' }}</td>
                      <td>{{ g.sname }}</td>
                      <td class="num">{{ formatWon(g.amount) }}</td>
                      <td class="num">{{ g.count }}</td>
                    </tr>
                  </tbody>
                </table>
              </details>
            </div>
          </section>

          <!-- 계정 섹션 -->
          <section v-for="acc in accounts" :id="`sec-${acc.id}`" :key="acc.id" class="aar-acc">
            <button type="button" class="aar-acc-head" @click="toggleExpand(acc.id)">
              <v-icon size="18" class="aar-chevron" :class="{ open: isExpanded(acc.id) }">
                mdi-chevron-down
              </v-icon>
              <span class="aar-acc-gubun" :class="acc.gubun === 'IN' ? 'is-income' : 'is-expense'">
                {{ acc.gubunLabel }}
              </span>
              <span class="aar-acc-title">
                <span v-if="data.showAccountCodes && acc.scode" class="aar-acc-code">{{ acc.scode }}</span>
                {{ acc.sname }}
              </span>
              <span class="aar-acc-meta">{{ acc.count }}건</span>
              <span class="aar-acc-amount">{{ formatWon(acc.amount) }}</span>
            </button>
            <div v-show="isExpanded(acc.id)" class="aar-acc-body">
              <table v-if="(acc.entries || []).length" class="aar-table">
                <thead>
                  <tr>
                    <th>일자</th>
                    <th>적요</th>
                    <th class="num">{{ acc.gubun === 'IN' ? '수입' : '지출' }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(entry, i) in acc.entries" :key="i">
                    <td class="nowrap">{{ entry.date || '-' }}</td>
                    <td class="memo">{{ entry.memo || '' }}</td>
                    <td class="num">{{ formatWon(acc.gubun === 'IN' ? (entry.income ?? entry.amount) : (entry.expense ?? entry.amount)) }}</td>
                  </tr>
                </tbody>
              </table>
              <p v-else class="aar-empty">해당 계정 내역 없음</p>
            </div>
          </section>

          <p v-if="!accounts.length" class="aar-empty is-page">조회된 계정 내역이 없습니다.</p>
        </main>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, nextTick } from 'vue';
import { bankbookApi } from '@/services/api';

const props = defineProps<{
  memberCode: string;
  year: string | number;
  month: string | number;
  bizGubun?: string;
  bankCode?: string | null;
}>();

const loading = ref(false);
const error = ref('');
const data = ref<any>(null);

const expandedIds = ref<Set<string>>(new Set());
const tocFilter = ref('');
const docRef = ref<HTMLElement | null>(null);

const formatWon = (value: number | null | undefined): string => {
  if (value == null || !Number.isFinite(Number(value))) return '-';
  return `${Math.round(Number(value)).toLocaleString('ko-KR')}원`;
};

const accounts = computed<any[]>(() => data.value?.accounts || []);
const incomeAccounts = computed(() => accounts.value.filter((a) => a.gubun === 'IN'));
const expenseAccounts = computed(() => accounts.value.filter((a) => a.gubun === 'OUT'));

const filteredIncome = computed(() =>
  incomeAccounts.value.filter((a) => !tocFilter.value || String(a.sname).includes(tocFilter.value))
);
const filteredExpense = computed(() =>
  expenseAccounts.value.filter((a) => !tocFilter.value || String(a.sname).includes(tocFilter.value))
);

const balance = computed(() =>
  Number(data.value?.summary?.incomeTotal || 0) - Number(data.value?.summary?.expenseTotal || 0)
);

const periodLabel = computed(() =>
  data.value?.periodRange || data.value?.periodLabel || ''
);

const laborRatioText = computed(() => {
  const d = data.value?.laborRatioDetail;
  if (!d) return '-';
  return d.ratio != null ? `${d.ratio}%` : '계산불가';
});

// 전체 섹션 id (인건비 + 계정들)
const allSectionIds = computed<string[]>(() => {
  const ids: string[] = [];
  if (data.value?.laborRatioDetail) ids.push('labor');
  accounts.value.forEach((acc) => ids.push(acc.id));
  return ids;
});

const allExpanded = computed(() =>
  allSectionIds.value.length > 0 && allSectionIds.value.every((id) => expandedIds.value.has(id))
);

const isExpanded = (id: string) => expandedIds.value.has(id);

const setExpanded = (id: string, on: boolean) => {
  const next = new Set(expandedIds.value);
  if (on) next.add(id);
  else next.delete(id);
  expandedIds.value = next;
};

const toggleExpand = (id: string) => {
  setExpanded(id, !isExpanded(id));
};

const scrollTo = async (id: string) => {
  setExpanded(id, true); // 목차에서 누르면 펼치고 스크롤 (다른 섹션 상태 유지)
  await nextTick();
  const el = docRef.value?.querySelector(`#sec-${id}`);
  el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const expandAll = () => {
  expandedIds.value = new Set(allSectionIds.value);
};

const collapseAll = () => {
  expandedIds.value = new Set();
};

const toggleAll = () => {
  if (allExpanded.value) collapseAll();
  else expandAll();
};

const load = async () => {
  if (!props.memberCode || !props.year || !props.month) return;
  loading.value = true;
  error.value = '';
  data.value = null;
  tocFilter.value = '';
  try {
    const monthNum = parseInt(String(props.month), 10);
    const response = await bankbookApi.getAccountingAnalysisReference(
      String(props.memberCode),
      {
        year: String(props.year),
        month: String(monthNum),
        bizGubun: props.bizGubun || undefined,
        bankCode: props.bankCode || undefined,
        periodType: 'month',
        startMonth: monthNum,
        endMonth: monthNum,
      }
    );
    const result = response.data?.data;
    if (!result) {
      error.value = '계정별내역 데이터가 없습니다.';
      return;
    }
    data.value = result;
    // 기본: 모두 펼친 상태
    const ids: string[] = [];
    if (result.laborRatioDetail) ids.push('labor');
    (result.accounts || []).forEach((acc: any) => ids.push(acc.id));
    expandedIds.value = new Set(ids);
  } catch (err: any) {
    error.value = err.response?.data?.message || err.message || '계정별내역 조회 실패';
  } finally {
    loading.value = false;
  }
};

watch(
  () => [props.memberCode, props.year, props.month, props.bizGubun, props.bankCode],
  () => { load(); },
  { immediate: true }
);
</script>

<!-- 스타일은 main.scss에서 pages/accounting-analysis-reference-embed 를 import -->
